'use client';
import { useCallback, useMemo } from 'react';

import { useLoggerRootContext } from '../Logger';
import { type LogParameters, type Logger } from '../models';

export const useLogger = (pageName: string): Logger => {
  const { trackEvent, debug, metadata, isInitialized, appName } = useLoggerRootContext();

  const view = useCallback(
    (parameters?: LogParameters): Promise<void> | null => {
      if (debug === true) {
        console.table({ pageName, ...metadata, ...parameters });
        return Promise.resolve();
      }

      try {
        trackEvent('page_view', {
          page_title: pageName,
          app_name: appName,
          ...metadata,
          ...parameters,
        });
        return Promise.resolve();
      } catch (error) {
        console.error('Error tracking page view:', error);
        return null;
      }
    },
    [trackEvent, pageName, metadata, debug, appName],
  );

  const event = useCallback(
    (
      title: string,
      action: string,
      parameters?: LogParameters,
      _options?: { useKlaviyo?: boolean },
    ): Promise<void> | null => {
      if (debug === true) {
        const logPayload = { title, action, pageName, ...metadata, ...parameters, options: _options };
        console.table(logPayload);
        return Promise.resolve();
      }

      try {
        trackEvent(title, {
          action,
          pageName,
          ...metadata,
          ...parameters,
        });
        return Promise.resolve();
      } catch (error) {
        console.error('Error tracking event:', error);
        return null;
      }
    },
    [trackEvent, pageName, metadata, debug],
  );

  const actionEvent = useMemo(() => {
    function createActionEvent(action: string) {
      return (name: string, parameters?: LogParameters, options?: { useKlaviyo?: boolean }): Promise<void> | null => {
        return event(name, action, parameters, options);
      };
    }

    return {
      click: createActionEvent('clicked'),
      confirmed: createActionEvent('confirmed'),
      created: createActionEvent('created'),
      updated: createActionEvent('updated'),
      selected: createActionEvent('selected'),
      filled: createActionEvent('filled'),
      impressed: createActionEvent('impressed'),
      unimpressed: createActionEvent('unimpressed'),
      searched: createActionEvent('searched'),
    };
  }, [event]);

  return useMemo(() => {
    return { view, event, isInitialized, ...actionEvent };
  }, [actionEvent, event, view, isInitialized]);
};
